import {
  useCallback,
  forwardRef,
  CSSProperties,
  MouseEventHandler,
  KeyboardEventHandler,
  PropsWithChildren,
  RefCallback,
} from "react";
import { AccessibilityProps } from "../../AccessibilityProps";

/**
 * The `HeaderWrapper` component properties.
 */
export interface HeaderWrapperProps extends Pick<AccessibilityProps, "aria-sort" | "aria-label"> {

  /**
   * The reference passed from the tooltip.
   */
  readonly tooltipRef?: RefCallback<HTMLTableCellElement>;

  /**
   * The CSS class name.
   */
  readonly className?: string;

  /**
   * The style.
   */
  readonly style?: CSSProperties;

  /**
   * The tab index.
   */
  readonly tabIndex?: number;

  /**
   * Called when the header is clicked.
   */
  readonly onClick?: MouseEventHandler<HTMLTableCellElement>;

  /**
   * Called when the mouse is pressed down on the header.
   */
  readonly onMouseDown?: MouseEventHandler<HTMLTableCellElement>;

  /**
   * Called when a key is pressed.
   */
  readonly onKeyPress?: KeyboardEventHandler<HTMLTableCellElement>;
}

/**
 * A wrapper for the header.
 */
export const HeaderWrapper = forwardRef<HTMLTableCellElement, PropsWithChildren<HeaderWrapperProps>>(function HeaderWrapper(props, ref) {

  const {
    tooltipRef,
    className,
    style,
    tabIndex,
    children,
    onClick,
    onMouseDown,
    onKeyPress,
    "aria-label": ariaLabel,
    "aria-sort": ariaSort,
  } = props;

  const refHandler = useCallback((val: HTMLTableCellElement | null) => {
    tooltipRef?.(val);

    if (ref) {
      if (typeof ref === "function")
        ref(val);
      else
        ref.current = val;
    }
  }, [ref, tooltipRef]);

  return (
    <th
      ref={refHandler}
      className={className}
      style={style}
      tabIndex={tabIndex}
      onClick={onClick}
      aria-sort={ariaSort}
      aria-label={ariaLabel}
      onMouseDown={onMouseDown}
      onKeyPress={onKeyPress}
      scope="col">
      {children}
    </th>
  );
});
